import styled from "@emotion/styled";
import { AnimatePresence, motion } from "motion/react";
import {
  MDSButton,
  MDSButtonVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { ZIndex } from "@/domains/design/constants";
import { SidePanelLoaderBar } from "./side-panel-loader-bar";
import { useHeadsUpContext } from "@/components/heads-up/context/heads-up-context";
import { HeadsUpViewState } from "@/components/heads-up/context/heads-up-context";
import { type FC, useMemo, useCallback, useState, useEffect } from "react";
import { MdsIconKind } from "@/design-system/components/icon";
import { MdsDropdownContentList, MdsDropdownItemKind } from "@/design-system/components/dropdown";
import { HeadsUpFullEmptyView } from "./views/heads-up-full-empty-view";
import { HeadsUpGeneratingView } from "./views/heads-up-generating-view";
import { HeadsUpErrorView } from "./views/heads-up-error-view";
import { HeadsUpNoContentView } from "./views/heads-up-no-content-view";
import { HeadsUpContentView } from "./views/heads-up-content-view";
import { HeadsUpFindMoreSection } from "./find-more";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { HeadsUpSidebarHeader } from "@/components/heads-up/sidebar/header/heads-up-sidebar-header";
import { SIDE_PANEL_COLLAPSED_BUFFER_PX } from "@/app/router/constants";

export const HeadsUpSidebarContent: FC = () => {
  const { context, content, activity, interactions, resources, ui } = useHeadsUpContext();
  const { refresh, regenerate, findMore, openHeadsUp } = interactions;

  const canRefresh = activity.canRefresh;
  const canRegenerate = activity.canRegenerate;
  const showLoaderBar = activity.isRefreshing;
  const isFindMoreDisabled = activity.isFindingMore || !activity.canFindMore;

  const [isHeaderHovered, setIsHeaderHovered] = useState(false);

  const handleHeaderClick = useCallback(() => {
    if (!ui.isChatOverlayOpen) return;
    openHeadsUp();
  }, [openHeadsUp, ui.isChatOverlayOpen]);

  const handleHeaderMouseEnter = useCallback(() => {
    if (!ui.isChatOverlayOpen) return;
    setIsHeaderHovered(true);
  }, [ui.isChatOverlayOpen]);

  const handleHeaderMouseLeave = useCallback(() => {
    setIsHeaderHovered(false);
  }, []);

  useEffect(() => {
    if (!ui.isChatOverlayOpen) {
      setIsHeaderHovered(false);
    }
  }, [ui.isChatOverlayOpen]);

  const titleIsLoading = Boolean(
    context.activeNoteId && resources.isNoteLoading(context.activeNoteId)
  );
  const headerTitle = (() => {
    if (titleIsLoading) {
      return "Loading note…";
    }

    if (!context.activeNoteId) {
      return "No note selected";
    }

    return context.activeNoteTitle ?? UNTITLED_NOTE_TITLE;
  })();

  const moreMenuContent = useMemo<MdsDropdownContentList>(
    () => ({
      items: [
        {
          id: "regenerate",
          kind: MdsDropdownItemKind.Button,
          label: "Regenerate from scratch",
          iconKind: MdsIconKind.Refresh,
          onClick: () => regenerate(),
          isDisabled: !canRegenerate,
        },
      ],
    }),
    [canRegenerate, regenerate]
  );

  const renderCurrentView = useMemo(() => {
    switch (content.viewState) {
      case HeadsUpViewState.FullEmpty:
        return <HeadsUpFullEmptyView />;
      case HeadsUpViewState.Generating:
        return <HeadsUpGeneratingView />;
      case HeadsUpViewState.Error:
        return <HeadsUpErrorView message={content.error} onRetry={regenerate} />;
      case HeadsUpViewState.GeneratedNoContent:
        return <HeadsUpNoContentView />;
      case HeadsUpViewState.GeneratedHasContent:
      default:
        return (
          <HeadsUpContentView
            cards={content.primaryCards}
            cardRenderOptions={resources.cardRenderOptions}
          />
        );
    }
  }, [
    content.error,
    content.primaryCards,
    content.viewState,
    regenerate,
    resources.cardRenderOptions,
  ]);

  const animationKey = context.contextId ?? "heads-up";
  const pendingSnapshot = content.pendingSnapshot;

  return (
    <Container $isHoveringHeader={isHeaderHovered && ui.isChatOverlayOpen}>
      <HeaderTopBar
        type="button"
        onClick={handleHeaderClick}
        disabled={!ui.isChatOverlayOpen}
        aria-disabled={!ui.isChatOverlayOpen}
        $isInteractive={ui.isChatOverlayOpen}
        $isCollapsed={!ui.isChatOverlayOpen}
        onMouseEnter={handleHeaderMouseEnter}
        onMouseLeave={handleHeaderMouseLeave}
      >
        <MDSText
          variant={MDSTextVariant.TextSmd}
          weight={MDSTextWeight.SemiBold}
          color={MDSTextColor.Contrast}
        >
          Heads Up
        </MDSText>
      </HeaderTopBar>

      <SidePanelLoaderBar
        active={showLoaderBar}
        inset={24}
        isChatOverlayOpen={ui.isChatOverlayOpen}
      />

      <HeadsUpSidebarHeader
        viewState={content.viewState}
        formattedRelativeTime={content.formattedRelativeTime}
        headerTitle={headerTitle}
        onRefresh={refresh}
        canRefresh={canRefresh}
        canRegenerate={canRegenerate}
        moreMenuContent={moreMenuContent}
        activeNoteId={context.activeNoteId ?? null}
      />

      <Body>
        {pendingSnapshot ? (
          <PendingUpdatesContainer>
            <MDSButton variant={MDSButtonVariant.Emphasis} onClick={pendingSnapshot.accept}>
              See new changes
            </MDSButton>
          </PendingUpdatesContainer>
        ) : null}
        <AnimatePresence mode="wait">
          <AnimatedView
            key={animationKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {renderCurrentView}

            <HeadsUpFindMoreSection
              viewState={content.viewState}
              onFindMore={findMore}
              isFindingMore={activity.isFindingMore}
              isDisabled={isFindMoreDisabled}
              hasFoundMore={activity.hasFoundMore}
              findMoreCards={content.findMoreCards}
              cardRenderOptions={resources.cardRenderOptions}
            />
          </AnimatedView>
        </AnimatePresence>
      </Body>
    </Container>
  );
};

const Container = styled.div<{ $isHoveringHeader: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme, $isHoveringHeader }) => {
    if ($isHoveringHeader) {
      return theme.colors.surface.headsUp.hover;
    }

    return theme.colors.surface.headsUp.default;
  }};
  transition: background-color ${({ theme }) => theme.transitions.background};
  user-select: none;
  border-top-right-radius: ${({ theme }) => theme.borderRadius.xlarge};
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-bottom: calc(200px + ${SIDE_PANEL_COLLAPSED_BUFFER_PX}px);
  position: relative;
`;

const AnimatedView = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const PendingUpdatesContainer = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: ${ZIndex.Dropdown};

  & > * {
    pointer-events: auto;
  }
`;

const HeaderTopBar = styled.button<{ $isInteractive: boolean; $isCollapsed: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-block: ${({ theme }) => theme.spacing["3"]};
  padding-inline: ${({ theme }) => theme.spacing["5"]};

  border-bottom: 1px solid
    ${({ theme, $isCollapsed }) =>
      !$isCollapsed ? "rgba(0, 0, 0, 0)" : theme.colors.border.onAccent};
  transition:
    border-color 0.16s ease,
    background-color 0.08s ease;
  width: 100%;
  text-align: left;
  font: inherit;
  cursor: ${({ $isInteractive }) => ($isInteractive ? "pointer" : "default")};

  &:disabled {
    cursor: default;
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 2px;
  }
`;
